import { useEffect, useMemo, useState } from "react";
import {
    Users, UserPlus, LogOut, Search, Tag, Edit3, Trash2,
    Wheat, Salad, Baby, User, Plus, X, Loader2, AlertCircle,
} from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import {
    adminApi,
    DIET_OPTIONS, GUEST_TYPES, dietLabel, typeLabel,
} from "@/lib/admin";
import { BrandLogo, BrandIcon } from "@/components/Brand";

const TYPE_ICON = { adult: User, child: Users, baby: Baby };

export default function AdminDashboard({ wedding, onLogout }) {
    const [guests, setGuests] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [attendingFilter, setAttendingFilter] = useState("all");
    const [tagFilter, setTagFilter] = useState(null);
    const [modal, setModal] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const [list, st] = await Promise.all([
                adminApi.listGuests(),
                adminApi.stats(),
            ]);
            setGuests(list.guests);
            setStats(st);
        } catch (e) {
            console.error("admin dashboard load failed", e);
            setError("No se pudieron cargar los datos.");
            // If the session expired (401), bubble up so the wrapper shows login.
            if (e?.response?.status === 401) {
                onLogout?.();
            }
        } finally {
            setLoading(false);
        }
    };

    // Load guests + stats on mount.
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return guests.filter((g) => {
            if (typeFilter !== "all" && g.guest_type !== typeFilter) return false;
            if (attendingFilter === "yes" && !g.attending) return false;
            if (attendingFilter === "no" && g.attending) return false;
            if (tagFilter && !(g.tags || []).includes(tagFilter)) return false;
            if (q) {
                const hay = [g.name, g.allergies, g.notes, ...(g.tags || [])]
                    .join(" ").toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [guests, search, typeFilter, attendingFilter, tagFilter]);

    const handleSave = async (payload, guestId) => {
        try {
            if (guestId) await adminApi.updateGuest(guestId, payload);
            else await adminApi.createGuest(payload);
            setModal(null);
            await load();
        } catch (e) {
            alert("No se pudo guardar.");
        }
    };

    const handleDelete = async (g) => {
        if (!confirm(`¿Borrar a ${g.name}?`)) return;
        try {
            await adminApi.deleteGuest(g.id);
            await load();
        } catch (e) {
            alert("No se pudo borrar.");
        }
    };

    const tagsAvailable = useMemo(
        () => Object.keys(stats?.tags || {}).sort(),
        [stats],
    );

    return (
        <div data-testid={ADMIN.dashboardRoot} className="min-h-[100dvh] bg-[var(--bg)]">
            <header className="px-6 md:px-10 py-5 border-b border-[var(--line)] bg-[var(--bg)]/85 backdrop-blur sticky top-0 z-20 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <a href="/" className="flex items-center gap-2 shrink-0">
                        <BrandIcon
                            variant="dark"
                            style={{ height: 30, width: "auto" }}
                        />
                        <BrandLogo
                            variant="dark"
                            style={{ height: 14, width: "auto" }}
                            className="hidden md:block"
                        />
                    </a>
                    <div className="border-l border-[var(--line)] pl-4">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--ink-soft)]">
                            Panel de los novios
                        </div>
                        <h1 className="font-serif text-xl md:text-2xl leading-none mt-1">
                            {wedding?.primary_name || ""} & {wedding?.secondary_name || ""}
                        </h1>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {wedding?.published && wedding?.slug && (
                        <a href={`/w/${wedding.slug}`} target="_blank" rel="noreferrer"
                           className="btn-ghost" style={{ padding: "8px 14px", fontSize: 12 }}>
                            Ver web
                        </a>
                    )}
                    <a
                        href="/editor"
                        data-testid="admin-edit-web"
                        className="btn-ghost"
                        style={{ padding: "8px 14px", fontSize: 12 }}
                    >
                        Editar mi web
                    </a>
                    <button
                        data-testid={ADMIN.logoutBtn}
                        onClick={() => onLogout?.()}
                        className="btn-ghost"
                        style={{ padding: "8px 14px", fontSize: 12 }}
                    >
                        <LogOut size={14} />
                        Salir
                    </button>
                </div>
            </header>

            <main className="px-6 md:px-10 py-7 max-w-[1200px] mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    <StatCard testId={ADMIN.statTotal} label="Confirmados" big={stats?.attending || 0} sub={`de ${stats?.total || 0}`} />
                    <StatCard testId={ADMIN.statAdults} label="Adultos" big={stats?.by_type?.adult || 0} icon={User} />
                    <StatCard testId={ADMIN.statChildren} label="Niños" big={stats?.by_type?.child || 0} icon={Users} />
                    <StatCard testId={ADMIN.statBabies} label="Bebés" big={stats?.by_type?.baby || 0} icon={Baby} />
                    <StatCard testId={ADMIN.statAllergies} label="Con alergias" big={stats?.allergies_count || 0} icon={AlertCircle} />
                </div>

                {stats && Object.keys(stats.diet_counts || {}).length > 0 && (
                    <div className="card-soft px-5 py-4 mb-6">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-3 flex items-center gap-2">
                            <Salad size={12} /> Necesidades especiales (para el catering)
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(stats.diet_counts).map(([k, v]) => (
                                <span key={k} data-testid={ADMIN.statDiet(k)} className="chip"
                                      style={{ background: "var(--bg-soft)", borderColor: "transparent" }}>
                                    <Wheat size={11} />
                                    {dietLabel(k)} <strong className="text-[var(--ink)]">· {v}</strong>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <div className="card-soft p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="relative flex-1">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
                        <input
                            data-testid={ADMIN.searchField}
                            type="text"
                            className="input-soft"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar por nombre, etiqueta, alergias..."
                            style={{ paddingLeft: 36, paddingTop: 11, paddingBottom: 11 }}
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <FilterChip label="Todos" active={typeFilter === "all"} onClick={() => setTypeFilter("all")} testId={ADMIN.typeFilter("all")} />
                        {GUEST_TYPES.map((t) => (
                            <FilterChip key={t.id} label={t.label}
                                active={typeFilter === t.id}
                                onClick={() => setTypeFilter(t.id)}
                                testId={ADMIN.typeFilter(t.id)} />
                        ))}
                        <span className="w-px bg-[var(--line)] mx-1" />
                        <FilterChip label="Asisten" active={attendingFilter === "yes"} onClick={() => setAttendingFilter(attendingFilter === "yes" ? "all" : "yes")} />
                        <FilterChip label="No asisten" active={attendingFilter === "no"} onClick={() => setAttendingFilter(attendingFilter === "no" ? "all" : "no")} />
                    </div>
                    <button data-testid={ADMIN.addGuestBtn} onClick={() => setModal({ mode: "add" })} className="btn-primary">
                        <UserPlus size={14} />
                        Añadir invitado
                    </button>
                </div>

                {tagsAvailable.length > 0 && (
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                        <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)] flex items-center gap-1">
                            <Tag size={11} /> Etiquetas:
                        </span>
                        {tagsAvailable.map((t) => (
                            <button key={t} onClick={() => setTagFilter(tagFilter === t ? null : t)}
                                    className={`px-3 py-1 text-xs rounded-full border transition-all ${
                                        tagFilter === t
                                            ? "bg-[var(--ink)] text-[#fbf9f6] border-[var(--ink)]"
                                            : "bg-white border-[var(--line)]"
                                    }`}>
                                {t} <span className="opacity-60">· {stats?.tags?.[t] || 0}</span>
                            </button>
                        ))}
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-16 text-[var(--ink-soft)]">
                        <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                        Cargando invitados...
                    </div>
                ) : error ? (
                    <div className="text-center py-16 text-red-600">{error}</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 card-soft">
                        <Users size={28} className="mx-auto mb-3 opacity-50" />
                        <div className="font-serif text-2xl">
                            {guests.length === 0 ? "Aún no hay invitados" : "Sin resultados"}
                        </div>
                        <p className="text-sm text-[var(--ink-soft)] mt-2">
                            {guests.length === 0
                                ? "Compartid vuestra web o añadid invitados manualmente."
                                : "Cambia los filtros para ver más."}
                        </p>
                    </div>
                ) : (
                    <div className="card-soft overflow-hidden">
                        <div className="hidden md:grid grid-cols-[1.6fr_0.9fr_0.7fr_1.4fr_1fr_auto] gap-3 px-5 py-3 text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)] border-b border-[var(--line)]">
                            <span>Nombre</span><span>Tipo</span><span>Asiste</span><span>Dieta · alergias</span><span>Etiquetas</span><span></span>
                        </div>
                        {filtered.map((g) => (
                            <GuestRow key={g.id} guest={g}
                                onEdit={() => setModal({ mode: "edit", guest: g })}
                                onDelete={() => handleDelete(g)} />
                        ))}
                    </div>
                )}
            </main>

            {modal && (
                <GuestModal mode={modal.mode} guest={modal.guest}
                    onClose={() => setModal(null)}
                    onSave={(payload) => handleSave(payload, modal.guest?.id)}
                    knownTags={tagsAvailable} />
            )}
        </div>
    );
}

function StatCard({ label, big, sub, icon: Icon, testId }) {
    return (
        <div data-testid={testId} className="card-soft p-4">
            <div className="flex items-center justify-between text-[var(--ink-soft)]">
                <span className="text-[11px] uppercase tracking-[0.18em]">{label}</span>
                {Icon && <Icon size={14} className="opacity-50" />}
            </div>
            <div className="font-serif text-3xl mt-2 leading-none">{big}</div>
            {sub && <div className="text-xs text-[var(--ink-soft)] mt-1">{sub}</div>}
        </div>
    );
}

function FilterChip({ label, active, onClick, testId }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`px-3 py-2 text-xs rounded-full border transition-all ${
                active
                    ? "bg-[var(--ink)] text-[#fbf9f6] border-[var(--ink)]"
                    : "bg-white border-[var(--line)] hover:border-[var(--gold-soft)]"
            }`}>
            {label}
        </button>
    );
}

function GuestRow({ guest, onEdit, onDelete }) {
    const Icon = TYPE_ICON[guest.guest_type] || User;
    return (
        <div data-testid={ADMIN.guestRow(guest.id)}
             className="grid grid-cols-1 md:grid-cols-[1.6fr_0.9fr_0.7fr_1.4fr_1fr_auto] gap-2 md:gap-3 items-start md:items-center px-5 py-4 border-b border-[var(--line)] last:border-b-0">
            <div className="flex items-center gap-2">
                <Icon size={14} className="text-[var(--ink-soft)] shrink-0" />
                <span className="font-medium">{guest.name}</span>
                {guest.plus_one && (
                    <span className="chip text-[10px]" style={{ padding: "2px 8px" }}>+1</span>
                )}
            </div>
            <div className="text-sm">{typeLabel(guest.guest_type)}</div>
            <div className="text-sm">
                {guest.attending ? (
                    <span className="chip" style={{ background: "#e8f3ec", borderColor: "transparent", color: "#1c5b3a" }}>Sí</span>
                ) : (
                    <span className="chip" style={{ background: "#f7ebeb", borderColor: "transparent", color: "#8d2222" }}>No</span>
                )}
            </div>
            <div className="text-xs text-[var(--ink-soft)]">
                {(guest.diet_restrictions || []).map(dietLabel).join(" · ") || "—"}
                {guest.allergies && (
                    <div className="text-red-700 mt-0.5">⚠ {guest.allergies}</div>
                )}
            </div>
            <div className="flex flex-wrap gap-1">
                {(guest.tags || []).map((t) => (
                    <span key={t} className="chip text-[10px]" style={{ padding: "2px 8px" }}>{t}</span>
                ))}
            </div>
            <div className="flex items-center gap-1">
                <button data-testid={ADMIN.guestEdit(guest.id)} onClick={onEdit}
                        className="w-8 h-8 rounded-full border border-[var(--line)] inline-flex items-center justify-center hover:bg-[var(--bg-soft)]"
                        aria-label="Editar">
                    <Edit3 size={13} />
                </button>
                <button data-testid={ADMIN.guestDelete(guest.id)} onClick={onDelete}
                        className="w-8 h-8 rounded-full border border-[var(--line)] inline-flex items-center justify-center hover:bg-red-50 hover:text-red-600"
                        aria-label="Borrar">
                    <Trash2 size={13} />
                </button>
            </div>
        </div>
    );
}

function GuestModal({ mode, guest, onClose, onSave, knownTags }) {
    const [form, setForm] = useState({
        name: guest?.name || "",
        guest_type: guest?.guest_type || "adult",
        attending: guest?.attending ?? true,
        plus_one: guest?.plus_one || false,
        plus_one_name: guest?.plus_one_name || "",
        diet_restrictions: guest?.diet_restrictions || [],
        allergies: guest?.allergies || "",
        tags: guest?.tags || [],
        notes: guest?.notes || "",
    });
    const [tagInput, setTagInput] = useState("");

    const toggleDiet = (id) => setForm((p) => ({
        ...p,
        diet_restrictions: p.diet_restrictions.includes(id)
            ? p.diet_restrictions.filter((d) => d !== id)
            : [...p.diet_restrictions, id],
    }));

    const addTag = (t) => {
        const clean = (t || "").trim();
        if (!clean || form.tags.includes(clean)) return;
        setForm((p) => ({ ...p, tags: [...p.tags, clean] }));
        setTagInput("");
    };

    const removeTag = (t) => setForm((p) => ({ ...p, tags: p.tags.filter((x) => x !== t) }));

    const submit = (e) => {
        e?.preventDefault();
        if (!form.name.trim()) return;
        onSave({ ...form, name: form.name.trim() });
    };

    return (
        <div data-testid={ADMIN.guestModal}
             className="fixed inset-0 z-[70] bg-[rgba(20,16,12,0.55)] backdrop-blur-sm flex md:items-center items-end justify-center">
            <div className="bg-[var(--bg)] w-full md:max-w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl md:m-6 max-h-[94dvh] overflow-y-auto tpl-anim">
                <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-[var(--line)] bg-[var(--bg)]/95 backdrop-blur">
                    <h2 className="font-serif text-2xl">
                        {mode === "edit" ? "Editar invitado" : "Añadir invitado"}
                    </h2>
                    <button data-testid={ADMIN.guestModalClose} onClick={onClose}
                            className="w-10 h-10 rounded-full border border-[var(--line)] inline-flex items-center justify-center bg-white">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={submit} noValidate className="px-6 py-6 space-y-5">
                    <div>
                        <label className="label-soft">Nombre</label>
                        <input type="text" className="input-soft" value={form.name}
                               onChange={(e) => setForm({ ...form, name: e.target.value })}
                               placeholder="Nombre y apellido" autoFocus />
                    </div>

                    <div>
                        <label className="label-soft">Tipo</label>
                        <div className="grid grid-cols-3 gap-2">
                            {GUEST_TYPES.map((t) => (
                                <button type="button" key={t.id}
                                        onClick={() => setForm({ ...form, guest_type: t.id })}
                                        className={`px-3 py-2.5 rounded-xl border text-sm ${
                                            form.guest_type === t.id
                                                ? "bg-[var(--ink)] text-[#fbf9f6] border-[var(--ink)]"
                                                : "bg-white border-[var(--line)]"
                                        }`}>
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="label-soft">Asiste</label>
                        <div className="grid grid-cols-2 gap-2">
                            <button type="button" onClick={() => setForm({ ...form, attending: true })}
                                    className={`px-3 py-2.5 rounded-xl border text-sm ${
                                        form.attending
                                            ? "bg-[var(--ink)] text-[#fbf9f6] border-[var(--ink)]"
                                            : "bg-white border-[var(--line)]"
                                    }`}>Sí</button>
                            <button type="button" onClick={() => setForm({ ...form, attending: false })}
                                    className={`px-3 py-2.5 rounded-xl border text-sm ${
                                        !form.attending
                                            ? "bg-[var(--ink)] text-[#fbf9f6] border-[var(--ink)]"
                                            : "bg-white border-[var(--line)]"
                                    }`}>No</button>
                        </div>
                    </div>

                    <div>
                        <label className="flex items-center gap-3">
                            <input type="checkbox" checked={form.plus_one}
                                   onChange={(e) => setForm({ ...form, plus_one: e.target.checked })}
                                   className="w-5 h-5 accent-[var(--ink)]" />
                            <span className="text-sm">Con acompañante</span>
                        </label>
                        {form.plus_one && (
                            <input type="text" className="input-soft mt-3"
                                   value={form.plus_one_name}
                                   onChange={(e) => setForm({ ...form, plus_one_name: e.target.value })}
                                   placeholder="Nombre del acompañante" />
                        )}
                    </div>

                    <div>
                        <label className="label-soft">Dieta</label>
                        <div className="flex flex-wrap gap-2">
                            {DIET_OPTIONS.map((d) => {
                                const on = form.diet_restrictions.includes(d.id);
                                return (
                                    <button type="button" key={d.id} onClick={() => toggleDiet(d.id)}
                                            className={`px-3 py-1.5 rounded-full border text-xs ${
                                                on
                                                    ? "bg-[var(--ink)] text-[#fbf9f6] border-[var(--ink)]"
                                                    : "bg-white border-[var(--line)]"
                                            }`}>
                                        {d.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="label-soft">Alergias</label>
                        <input type="text" className="input-soft" value={form.allergies}
                               onChange={(e) => setForm({ ...form, allergies: e.target.value })}
                               placeholder="Ej. frutos secos, marisco..." />
                    </div>

                    <div>
                        <label className="label-soft">Etiquetas</label>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {form.tags.map((t) => (
                                <span key={t} className="chip flex items-center gap-1">
                                    {t}
                                    <button type="button" onClick={() => removeTag(t)}
                                            className="ml-1 opacity-70" aria-label="Quitar etiqueta">
                                        <X size={11} />
                                    </button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input type="text" list="known-tags" className="input-soft flex-1"
                                   value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                                   onKeyDown={(e) => {
                                       if (e.key === "Enter") {
                                           e.preventDefault();
                                           addTag(tagInput);
                                       }
                                   }}
                                   placeholder="Familia del novio, universidad..." />
                            <datalist id="known-tags">
                                {knownTags.map((t) => <option key={t} value={t} />)}
                            </datalist>
                            <button type="button" onClick={() => addTag(tagInput)}
                                    className="btn-ghost" style={{ padding: "12px 14px" }}>
                                <Plus size={14} />
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="label-soft">Notas</label>
                        <textarea className="input-soft" rows={3} value={form.notes}
                                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                  placeholder="Cualquier detalle relevante" />
                    </div>

                    <button data-testid={ADMIN.guestSave} type="submit" className="btn-primary w-full">
                        Guardar
                    </button>
                </form>
            </div>
        </div>
    );
}
